import { useState } from "react";
import { MessageCircle, Minus, Plus, ShoppingBag, Check } from "lucide-react";
import { menuCategories, WA_NUMBER } from "../data/menuData";
import { useCart } from "../context/CartContext";

const badgeColors = {
  Bestseller: "bg-[#D92525] text-white",
  Spicy: "bg-black text-[#FFB800]",
  Signature: "bg-black text-[#FFB800]",
  Family: "bg-[#FFB800] text-black",
  Party: "bg-[#D92525] text-white",
  Cheesy: "bg-[#FFB800] text-black",
  Popular: "bg-[#25D366] text-white",
};

const buildItemWaLink = (item, qty) => {
  const lineTotal = item.price != null ? ` (₹${item.price * qty})` : "";
  const msg = `Hi KKFC Bangarpet! I would like to order:\n• ${item.name} × ${qty}${lineTotal}`;
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
};

function QtyStepper({ qty, setQty, testId }) {
  return (
    <div className="inline-flex items-stretch brutal-border bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => setQty(Math.max(1, qty - 1))}
        aria-label="decrease"
        className="px-2.5 py-1.5 hover:bg-[#FFB800] disabled:opacity-40"
        disabled={qty <= 1}
      >
        <Minus size={14} strokeWidth={3} />
      </button>
      <div className="px-3 py-1.5 font-display text-lg leading-none border-x-2 border-black min-w-[40px] text-center">
        {qty}
      </div>
      <button
        type="button"
        onClick={() => setQty(Math.min(99, qty + 1))}
        aria-label="increase"
        className="px-2.5 py-1.5 hover:bg-[#FFB800]"
      >
        <Plus size={14} strokeWidth={3} />
      </button>
    </div>
  );
}

function MenuItemCard({ item, bg, isVeg }) {
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const { addItem, setOpen, qtyOf } = useCart();
  const inCart = qtyOf(item.name);
  const idSlug = item.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleAdd = () => {
    addItem(item, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };
  return (
    <div
      data-testid={`menu-item-${idSlug}`}
      className="brutal-border brutal-shadow bg-white p-5 flex flex-col justify-between"
      style={{ backgroundColor: bg }}
    >
      <div>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2 flex-1">
            {isVeg && (
              <div className="w-5 h-5 border-2 border-green-600 flex items-center justify-center shrink-0 mt-0.5">
                <div className="w-2.5 h-2.5 rounded-full bg-green-600"></div>
              </div>
            )}
            <h3 className="font-display text-xl md:text-[24px] uppercase leading-tight text-black flex-1">
              {item.name}
            </h3>
          </div>
          {item.price != null && (
            <div className="font-display text-xl md:text-2xl text-[#D92525] whitespace-nowrap">
              ₹{item.price}
            </div>
          )}
        </div>
        {item.badge && (
          <span
            className={`inline-block mt-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest brutal-border ${badgeColors[item.badge] || "bg-[#FFB800] text-black"}`}
          >
            {item.badge}
          </span>
        )}
        <p className="mt-3 text-sm text-neutral-800 leading-relaxed">
          {item.desc}
        </p>
      </div>
      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-700">
            Qty
          </span>
          <QtyStepper qty={qty} setQty={setQty} testId={`qty-stepper-${idSlug}`} />
        </div>
        {item.price != null && qty > 1 && (
          <span className="text-xs font-bold text-neutral-800">
            Subtotal: <span className="text-[#D92525] font-display text-lg">₹{item.price * qty}</span>
          </span>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleAdd}
          data-testid={`add-to-cart-${idSlug}`}
          className={`inline-flex items-center justify-center gap-1.5 brutal-border font-bold px-3 py-2.5 text-xs uppercase tracking-widest transition-colors ${added ? "bg-[#25D366] text-black" : "bg-[#FFB800] text-black"}`}
        >
          {added ? (
            <>
              <Check size={14} strokeWidth={3} /> Added
            </>
          ) : (
            <>
              <ShoppingBag size={14} strokeWidth={3} />
              Add{inCart ? ` (${inCart})` : ""}
            </>
          )}
        </button>
        <a
          href={buildItemWaLink(item, qty)}
          target="_blank"
          rel="noreferrer"
          data-testid={`order-btn-${idSlug}`}
          className="inline-flex items-center justify-center gap-1.5 brutal-border bg-[#25D366] text-black font-bold px-3 py-2.5 text-xs uppercase tracking-widest"
        >
          <MessageCircle size={14} strokeWidth={3} />
          Order {qty}
        </a>
      </div>
    </div>
  );
}

export default function Menu() {
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="bg-[#FAFAFA] border-b-2 border-black"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14">
          <div>
            <div className="inline-block brutal-border bg-[#FFB800] px-3 py-1 text-xs font-bold uppercase tracking-widest mb-4">
              Our Menu
            </div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.95] text-black">
              Hungry? <br />
              <span className="text-[#D92525]">Take a look</span> 👇
            </h2>
          </div>
          <p className="font-body text-base md:text-lg text-neutral-700 max-w-md">
            Everything is <strong>fresh</strong>, <strong>crispy</strong> and{" "}
            <strong>made-to-order</strong>. Tap an item below — your WhatsApp
            order goes straight to the restaurant.
          </p>
        </div>

        <div className="space-y-16 md:space-y-20">
          {menuCategories.map((cat, idx) => (
            <div
              key={cat.id}
              data-testid={`menu-category-${cat.id}`}
              className="grid lg:grid-cols-12 gap-6 lg:gap-10"
            >
              {/* Category visual */}
              <div
                className={`lg:col-span-4 ${idx % 2 === 1 ? "lg:order-2" : ""}`}
              >
                <div className="brutal-border brutal-shadow-lg overflow-hidden aspect-[4/5] relative">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black text-white p-4 border-t-2 border-black">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#FFB800]">
                      {cat.tag}
                    </div>
                    <div className="font-display text-3xl md:text-4xl uppercase leading-tight">
                      {cat.title}
                    </div>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="lg:col-span-8 grid sm:grid-cols-2 gap-5">
                {cat.items.map((item) => (
                  <MenuItemCard key={item.name} item={item} bg={cat.bg} isVeg={cat.isVeg} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
